import { A11yModule, FocusKeyManager } from "@angular/cdk/a11y";
import {
  DOWN_ARROW,
  ENTER,
  ESCAPE,
  SPACE,
  TAB,
  UP_ARROW,
} from "@angular/cdk/keycodes";
import { OverlayModule } from "@angular/cdk/overlay";
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  QueryList,
  ViewChildren,
} from "@angular/core";
import { TestId } from "pax-ui/core";

import { UncontrolledSelectOption } from "../types/uncontrolled-select-option.interface";
import { UncontrolledSelectListOption } from "../uncontrolled-select-list-option";

@Component({
  selector: "pax-uncontrolled-select-list",
  standalone: true,
  templateUrl: "./uncontrolled-select-list.html",
  imports: [UncontrolledSelectListOption, A11yModule, OverlayModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UncontrolledSelectList implements AfterViewInit {
  @ViewChildren(UncontrolledSelectListOption)
  private _items!: QueryList<UncontrolledSelectListOption>;

  @Input({ required: true }) id!: string;
  @Input({ required: true }) width!: number;
  @Input({ required: true }) isOpen!: boolean;

  @Input() options: UncontrolledSelectOption[] | undefined = [];
  @Input() selectedValue: UncontrolledSelectOption | undefined;

  @Output() clicked = new EventEmitter();
  @Output() tabOut = new EventEmitter();

  private _keyManager!: FocusKeyManager<UncontrolledSelectListOption>;

  uncontrolledSelectList = TestId.UNCONTROLLED_SELECT_LIST;

  ngAfterViewInit(): void {
    this._keyManager = new FocusKeyManager(this._items)
      .withWrap()
      .withHomeAndEnd();
    this._keyManager.setActiveItem(this.selectedValueIndex || 0);
  }

  @HostListener("keydown", ["$event"])
  onKeydown(event: KeyboardEvent) {
    // Prevents the page from scrolling (which would be default behavior.)
    event.preventDefault();
    // This seems to be deprecated by current web standards, but the official
    // cdk still uses numbers instead of strings. This offers better type safety
    // and readability compared to untyped strings.
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- Not refactored due to the reason mentioned above.
    const keyCode = event.keyCode;

    if (keyCode === TAB || keyCode === ESCAPE) {
      this._keyManager.tabOut.next();
      this.tabOut.emit();
    } else if (keyCode === SPACE || keyCode === ENTER) {
      this.handleSelected();
    } else if (keyCode === DOWN_ARROW) {
      if (!this.isCurrentLast()) {
        this._keyManager.setNextItemActive();
      }
    } else if (keyCode === UP_ARROW) {
      if (!this.isCurrentFirst()) {
        this._keyManager.setPreviousItemActive();
      }
    } else {
      this._keyManager.onKeydown(event);
    }
  }

  handleSelected() {
    const activeIndex = this._keyManager.activeItemIndex;
    if (!activeIndex || !this.options) {
      return;
    }

    const activeItem = this.options[activeIndex];
    if (!activeItem) {
      return;
    }

    this.clicked.emit(activeItem.value);
  }

  getOptionId(value: string) {
    return `${this.id}-option-${value}`;
  }

  get emptyOption(): UncontrolledSelectOption {
    return {
      value: "empty",
      label: $localize`Keine Einträge vorhanden.`,
    };
  }

  private isCurrentFirst() {
    return this._keyManager.activeItemIndex === 0;
  }

  private isCurrentLast() {
    return (
      !!this._keyManager.activeItemIndex &&
      this._keyManager.activeItemIndex === this._items.length - 1
    );
  }

  private get selectedValueIndex() {
    return this.options?.findIndex(
      (option) => option.value === this.selectedValue?.value,
    );
  }
}
